import type { Firestore } from 'firebase-admin/firestore'
import { Timestamp } from 'firebase-admin/firestore'
import { createLogger } from '../../common/utils/logger.js'
import type {
  RewardConfig,
  RewardRule,
  RewardEventType,
  UpdateRewardRuleDTO
} from '@my-roadtrip/types'

const logger = createLogger('AdminRewardsService')

const REWARD_CONFIG_COLLECTION = 'reward_config'
const REWARD_CONFIG_DOC = 'admin'
const REWARD_EVENTS_COLLECTION = 'reward_events'

const DEFAULT_RULES: Record<RewardEventType, RewardRule> = {
  'plan:created': { event: 'plan:created', enabled: true, points: 10, fuel: -1, description: 'Plan créé' },
  'plan:completed': { event: 'plan:completed', enabled: true, points: 25, fuel: -2, description: 'Plan complété' },
  'plan:updated': { event: 'plan:updated', enabled: true, points: 4, fuel: -1, description: 'Plan mis à jour' },
  'plan:deleted': { event: 'plan:deleted', enabled: false, points: -5, fuel: 0, description: 'Plan supprimé' },
  'stage:created': { event: 'stage:created', enabled: true, points: 6, fuel: -1, description: 'Étape ajoutée' },
  'stage:completed': { event: 'stage:completed', enabled: true, points: 8, fuel: -1, description: 'Étape complétée' },
  'stage:updated': { event: 'stage:updated', enabled: true, points: 2, fuel: 0, description: 'Étape mise à jour' },
  'stage:deleted': { event: 'stage:deleted', enabled: false, points: -2, fuel: 0, description: 'Étape supprimée' },
  'ai:generation:completed': { event: 'ai:generation:completed', enabled: true, points: 12, fuel: -2, description: 'Génération IA terminée' },
  'ai:suggestion:received': { event: 'ai:suggestion:received', enabled: true, points: 6, fuel: -1, description: 'Suggestion IA reçue' },
  'offline:sync:completed': { event: 'offline:sync:completed', enabled: true, points: 5, fuel: 0, description: 'Sync hors ligne terminée' },
  'share:created': { event: 'share:created', enabled: true, points: 8, fuel: 0, description: 'Partage créé' },
  'share:accessed': { event: 'share:accessed', enabled: true, points: 4, fuel: 0, description: 'Partage consulté' }
}

function buildDefaultConfig(updatedBy?: string): RewardConfig {
  return {
    rules: DEFAULT_RULES,
    version: 1,
    updatedAt: new Date(),
    ...(updatedBy ? { updatedBy } : {})
  }
}

export class AdminRewardsService {
  constructor(private readonly db: Firestore) { }

  async getConfig(): Promise<RewardConfig> {
    const doc = await this.db.collection(REWARD_CONFIG_COLLECTION).doc(REWARD_CONFIG_DOC).get()
    if (!doc.exists) {
      const defaults = buildDefaultConfig()
      await this.saveConfig(defaults)
      return defaults
    }
    const data = doc.data() as RewardConfig
    // Firestore timestamps → Date
    if (data.updatedAt && (data.updatedAt as any).toDate) {
      data.updatedAt = (data.updatedAt as unknown as Timestamp).toDate()
    }
    return data
  }

  private async saveConfig(config: RewardConfig): Promise<void> {
    await this.db.collection(REWARD_CONFIG_COLLECTION).doc(REWARD_CONFIG_DOC).set(
      {
        ...config,
        updatedAt: Timestamp.fromDate(new Date(config.updatedAt))
      },
      { merge: true }
    )
  }

  async getAllRules(): Promise<Record<RewardEventType, RewardRule>> {
    const config = await this.getConfig()
    return config.rules
  }

  async getRule(event: RewardEventType): Promise<RewardRule> {
    const rules = await this.getAllRules()
    return rules[event] ?? DEFAULT_RULES[event]
  }

  async updateRule(event: RewardEventType, updates: UpdateRewardRuleDTO, actorId?: string): Promise<RewardRule> {
    const config = await this.getConfig()
    const current = config.rules[event] ?? { ...DEFAULT_RULES[event], event }
    const next: RewardRule = {
      ...current,
      ...updates,
      event
    }
    const nextConfig: RewardConfig = {
      ...config,
      rules: { ...config.rules, [event]: next },
      version: (config.version ?? 1) + 1,
      updatedAt: new Date(),
      updatedBy: actorId ?? config.updatedBy
    }
    await this.saveConfig(nextConfig)
    return next
  }

  async batchUpdateRules(
    updates: Array<{ event: RewardEventType; data: UpdateRewardRuleDTO }>,
    actorId?: string
  ): Promise<Record<RewardEventType, RewardRule>> {
    const config = await this.getConfig()
    const rules = { ...config.rules }
    for (const { event, data } of updates) {
      const current = rules[event] ?? { ...DEFAULT_RULES[event], event }
      rules[event] = { ...current, ...data, event }
    }
    const nextConfig: RewardConfig = {
      ...config,
      rules,
      version: (config.version ?? 1) + 1,
      updatedAt: new Date(),
      updatedBy: actorId ?? config.updatedBy
    }
    await this.saveConfig(nextConfig)
    return rules
  }

  async resetToDefaults(actorId?: string): Promise<RewardConfig> {
    const defaults = buildDefaultConfig(actorId)
    await this.saveConfig(defaults)
    return defaults
  }

  async getStatistics(days = 30): Promise<Record<RewardEventType, { count: number; totalPoints: number; totalFuel: number }>> {
    const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000)
    const snapshot = await this.db
      .collection(REWARD_EVENTS_COLLECTION)
      .where('timestamp', '>=', since)
      .limit(5000)
      .get()

    const base: Record<RewardEventType, { count: number; totalPoints: number; totalFuel: number }> = {} as any
    for (const key of Object.keys(DEFAULT_RULES) as RewardEventType[]) {
      base[key] = { count: 0, totalPoints: 0, totalFuel: 0 }
    }

    for (const doc of snapshot.docs) {
      const data = doc.data() as { event?: RewardEventType; points?: number; fuel?: number }
      if (!data.event || !(data.event in base)) continue
      base[data.event].count += 1
      base[data.event].totalPoints += Number(data.points ?? 0)
      base[data.event].totalFuel += Number(data.fuel ?? 0)
    }

    return base
  }
}
